/**
 * The tree inventory: the sections the report's options switch on, over one read of the crowns'
 * tile archive or, without one, of the tree GeoPackage (and the boundary, if bound), in tabs for the map, an overview (the summary, the
 * species and the maturity) and the analysis. Every viewer sees them, the link holder too (N13).
 */
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import type { FeatureCollection } from 'geojson';
import { Center, Loader, Stack, Tabs, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/components/EmptyState';
import { byKind, type LayerOptions, type ObjectKind, type TreeRules } from '@/map/layers';
import { PMTILES_LAYERS, pmtilesTileUrl } from '@/map/pmtiles';
import { MapView } from '@/map/MapView';
import type { KindReportProps } from './ReportPage';
import { AnalysisSection } from './trees/AnalysisSection';
import { enabledTabs, treesConfig, type TreesConfig } from './trees/config';
import { treeExtras } from './trees/extras';
import { MapFrame } from './MapFrame';
import { SelectionBox } from './trees/SelectionBox';
import { MaturitySection } from './trees/MaturitySection';
import { SpeciesSection } from './trees/SpeciesSection';
import { baseStats, NON_SPECIES, withMaturity, type TreeStats } from './trees/stats';
import { SummarySection } from './trees/SummarySection';
import { useTreesData } from './trees/useTreesData';
import { usePrinting } from './trees/usePrinting';
import { useTreesArchive } from './trees/useTreesArchive';
import { FALLBACK_MIN_DBH } from './trees/maturity';
import type { Tree } from './trees/types';

type TreesTab = 'map' | 'overview' | 'analysis';

const TOP_FIRST: ObjectKind[] = ['boundary', 'ortho_tree_analysis', 'orthophoto'];

/** The archive's points as GeoJSON again, for the map's list of genera and its first framing. */
function pointsOf(trees: Tree[]): FeatureCollection {
  return { type: 'FeatureCollection', features: trees.map((t) => ({ type: 'Feature', id: t.id, properties: { genus: t.genus }, geometry: { type: 'Point', coordinates: [t.lon, t.lat] } })) };
}

/** The summary, the species and the maturity; memoized, since it stays mounted behind the other tabs. */
const Overview = memo(function Overview({ stats, sections, showStrata }: { stats: TreeStats; sections: TreesConfig['sections']; showStrata: boolean }) {
  return (
    <Stack gap="xl">
      {sections.summary.enabled && <SummarySection stats={stats} prose={sections.summary.prose} showMaturity={sections.maturity.enabled} showStrata={showStrata} />}
      {sections.species.enabled && <SpeciesSection stats={stats} />}
      {sections.maturity.enabled && <MaturitySection stats={stats} />}
    </Stack>
  );
});

export function TreesReport({ refs, inputs = [], linkSource, onVectorError, onVectorProgress, options, onOptionsChange, tokenSource, config }: KindReportProps) {
  const { t } = useTranslation('reports');
  const [active, setActive] = useState<TreesTab | null>(null);
  const [shown, setShown] = useState<TreesTab[]>([]);
  const printing = usePrinting();
  const cfg = useMemo(() => treesConfig(config), [config]);
  const own = byKind(refs, TOP_FIRST);
  const thresholds = cfg.sections.maturity.thresholds;
  // Where the crowns have a tile archive the map draws from it, and the figures come from a forest
  // summary made from these crowns or from the archive's points: the crowns' GeoPackage is not
  // read (useTreesArchive.ts).
  const crownsRef = own.find((r) => r.object_kind === 'ortho_tree_analysis' && r.via === 'link');
  const bounded = inputs.some((i) => i.role === 'boundary' && i.available);
  const summaryObject = inputs.find((i) => i.role === 'forest_summary' && i.available)?.object;
  const [crownsObject, crownsPath] = [crownsRef?.object, crownsRef?.path];
  const crownsFile = useMemo(() => (crownsObject ? { object: crownsObject, path: crownsPath ?? '' } : undefined), [crownsObject, crownsPath]);
  const fromArchive = useTreesArchive(crownsFile, summaryObject, linkSource, bounded);
  // The crowns' GeoPackage only once the archive is ruled out, so its download never starts for nothing.
  const gpkgRefs = useMemo(() => {
    if (fromArchive.state === 'checking') return [];
    return fromArchive.state === 'ready' ? own.filter((r) => r !== crownsRef) : own;
  }, [fromArchive.state, own, crownsRef]);
  const data = useTreesData(gpkgRefs, linkSource, onVectorError, onVectorProgress, thresholds);
  const { trees, boundary, treesObject, boundaryObject, hullHa, server } = data;
  const summaryBase = fromArchive.state === 'ready' && fromArchive.from === 'summary' ? fromArchive.base : null;
  const archivePoints = fromArchive.state === 'ready' && fromArchive.from === 'points' ? fromArchive.points : null;
  const boundaryRead = !data.loading;
  const base = useMemo(() => {
    if (summaryBase) return summaryBase;
    if (archivePoints) return boundaryRead ? baseStats(archivePoints, boundary) : null;
    return trees ? baseStats(trees, boundary, hullHa) : null;
  }, [summaryBase, archivePoints, boundaryRead, trees, boundary, hullHa]);
  const stats = useMemo(() => (base ? withMaturity(base, thresholds) : null), [base, thresholds]);
  const extras = useMemo(() => (stats ? treeExtras(stats) : null), [stats]);
  // The crowns' maturity tags live in the tiles; changed thresholds retag them there and make
  // the map fetch the tiles again. The first thresholds went in with the load.
  const [crownVersion, setCrownVersion] = useState(0);
  const thresholdsKey = JSON.stringify(thresholds);
  const seenThresholds = useRef<string | null>(null);
  useEffect(() => {
    if (!server) return;
    if (seenThresholds.current === null) {
      seenThresholds.current = thresholdsKey;
      return;
    }
    if (seenThresholds.current === thresholdsKey) return;
    seenThresholds.current = thresholdsKey;
    let alive = true;
    void server.retag(JSON.parse(thresholdsKey) as typeof thresholds).then(() => alive && setCrownVersion((v) => v + 1));
    return () => {
      alive = false;
    };
  }, [server, thresholdsKey]);
  // The map draws the crowns from tiles; the points are here for the genera and the bounds.
  const vectors = useMemo(() => {
    const out: Record<string, FeatureCollection> = {};
    if (trees && treesObject) out[treesObject] = trees;
    if (boundary && boundaryObject) out[boundaryObject] = boundary;
    if (summaryBase && crownsRef) out[crownsRef.object] = pointsOf(summaryBase.trees);
    if (archivePoints && crownsRef) out[crownsRef.object] = archivePoints;
    return out;
  }, [trees, boundary, treesObject, boundaryObject, summaryBase, archivePoints, crownsRef]);
  // From the archive, the crowns carry no tags of the page's: the map applies the rules itself.
  const mapRefs = useMemo(() => {
    if (fromArchive.state !== 'ready' || !crownsRef) return own;
    const { archive } = fromArchive;
    const tiles = {
      url: pmtilesTileUrl(crownsRef.object),
      source: `${crownsRef.object}:pmtiles`,
      maxzoom: archive.header.maxZoom,
      layers: PMTILES_LAYERS.photo,
      promoteId: { [PMTILES_LAYERS.photo.points]: 'id', [PMTILES_LAYERS.photo.polygons]: 'id' },
    };
    return own.map((r) => (r === crownsRef ? { ...r, tiles } : r));
  }, [fromArchive, crownsRef, own]);
  const treeRules = useMemo<TreeRules | undefined>(() => {
    if (fromArchive.state !== 'ready' || !stats) return undefined;
    return {
      minDbh: Object.fromEntries(Object.entries(stats.thresholds).map(([g, target]) => [g, target.min])),
      fallback: FALLBACK_MIN_DBH,
      nonSpecies: [...NON_SPECIES],
      strata: stats.strata ? { lo: stats.strata.lo, hi: stats.strata.hi } : null,
    };
  }, [fromArchive.state, stats]);

  const s = cfg.sections;
  const analysisTabs = useMemo(() => enabledTabs(cfg), [cfg]);
  const withDefaults: LayerOptions = { colorBy: 'species', ...options, crownVersion, ...(treeRules ? { treeRules } : {}) };
  // The rules are the page's, not part of the view a viewer's browser keeps.
  const change = (next: LayerOptions) => {
    const rest = { ...next };
    delete rest.treeRules;
    onOptionsChange(rest);
  };
  const loading = data.loading || fromArchive.state === 'checking';

  if (!s.summary.enabled && !s.map.enabled && !s.species.enabled && !analysisTabs.length && !s.maturity.enabled) {
    return <EmptyState title={t('trees.nothingEnabled')} />;
  }

  const tabs: TreesTab[] = [];
  if (s.map.enabled) tabs.push('map');
  if (s.summary.enabled || s.species.enabled || s.maturity.enabled) tabs.push('overview');
  if (analysisTabs.length) tabs.push('analysis');
  const current = active && tabs.includes(active) ? active : tabs[0];
  if (current && !shown.includes(current)) setShown([...shown, current]);

  const panel = (tab: TreesTab) => {
    if (tab === 'map') {
      return (
        <>
          <Text className="print-only" fw={700} mb="xs">
            {t('trees.sections.map')}
          </Text>
          <MapFrame testId="report-trees" wide={!!options.wide} onWideChange={(wide) => change({ ...withDefaults, wide })}>
            <MapView
              refs={mapRefs}
              linkSource={linkSource}
              externalVectors
              vectors={vectors}
              options={withDefaults}
              onOptionsChange={change}
              tokenSource={tokenSource}
              treeExtras
            />
            {stats && extras && <SelectionBox stats={stats} extras={extras} filters={options.filters} />}
          </MapFrame>
        </>
      );
    }
    if (!stats) return null;
    if (tab === 'overview') {
      return <Overview stats={stats} sections={s} showStrata={analysisTabs.includes('strata')} />;
    }
    return <AnalysisSection stats={stats} tabs={analysisTabs} />;
  };

  return (
    <Stack gap="xl">
      {loading && (
        <Center py="md">
          <Loader color="forest" />
        </Center>
      )}
      {tabs.length === 1 ? (
        panel(tabs[0]!)
      ) : (
        // Every panel stays in the page for print CSS to unhide. A tab once shown keeps its content
        // behind the others: the map its view, the analysis its drawn maps and charts. The map is
        // there from the start, since it opens first and its data is read anyway.
        // `display-none`, not Mantine's default `<Activity>`: a hidden Activity runs its effects'
        // cleanups, which would remove the map and make a new one at the home view.
        <Tabs value={current} onChange={(v) => v && setActive(v as TreesTab)} keepMounted keepMountedMode="display-none" className="report-tabs">
          <Tabs.List mb="md">
            {tabs.map((tab) => (
              <Tabs.Tab key={tab} value={tab}>
                {t(`trees.tabs.${tab}`)}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          {tabs.map((tab) => (
            <Tabs.Panel key={tab} value={tab}>
              {(tab === 'map' || shown.includes(tab) || printing) && panel(tab)}
            </Tabs.Panel>
          ))}
        </Tabs>
      )}
    </Stack>
  );
}
